// src/pages/DeviceTypeManagement/CreateDeviceTypeDialog.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { deviceTypesApi } from '../../services/apiClient';

const CreateDeviceTypeDialog = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    friendlyName: '',
    FrontEndName: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.friendlyName || formData.friendlyName.trim().length < 2) {
      newErrors.friendlyName = 'Device type name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const response = await deviceTypesApi.create(formData);

      if (response.data.success) {
        onSuccess(response.data.data);
        setFormData({ friendlyName: '', FrontEndName: '' });
      }
    } catch (error) {
      console.error('Error creating device type:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to create device type',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ friendlyName: '', FrontEndName: '' });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Device Type</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            Create a new device type to manage its service associations.
          </Alert>

          <TextField
            fullWidth
            label="Device Type Name"
            value={formData.friendlyName}
            onChange={(e) => handleChange('friendlyName', e.target.value)}
            error={!!errors.friendlyName}
            helperText={errors.friendlyName || 'Enter a unique device type name (technical identifier)'}
            disabled={loading}
            required
          />

          <TextField
            fullWidth
            label="Front-End Name"
            value={formData.FrontEndName}
            onChange={(e) => handleChange('FrontEndName', e.target.value)}
            helperText="Display name shown to users (optional)"
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDeviceTypeDialog;
